import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { SearchInput } from './SearchInput';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from '../ui/skeleton';

export function DataTable({
  columns,
  data,
  loading = false,
  searchPlaceholder = 'Search...',
  onSearch,
  searchValue: externalSearchValue,
  searchKeys,
  onRowClick,
  pagination,
  hideSearch = false,
  testId = 'data-table',
  onExport,
  exporting = false,
  exportFilename = 'export',
  hideExport = false,
  compact,
  fixedLayout = false,
  toolbarFilter,
  filters,
  filterContent,
  toolbarExtra,
}) {
  const customFilter = toolbarFilter || filters || filterContent;
  // Internal search state used when no external onSearch is provided
  const [internalSearch, setInternalSearch] = useState('');
  const isControlled = typeof onSearch === 'function';
  const searchValue = isControlled ? (externalSearchValue || '') : internalSearch;

  // Client-side filtering when search is not externally controlled
  const filteredData = useMemo(() => {
    if (isControlled || !internalSearch.trim()) return data;
    const term = internalSearch.toLowerCase();
    // Determine which keys to search: explicit searchKeys prop, or all text-based column keys (excluding 'actions')
    const keys = searchKeys || columns.filter(c => c.key !== 'actions').map(c => c.key);
    return data.filter(row =>
      keys.some(key => {
        const val = row[key];
        return val != null && String(val).toLowerCase().includes(term);
      })
    );
  }, [data, internalSearch, isControlled, searchKeys, columns]);

  const displayData = isControlled ? data : filteredData;

  // Auto-compact padding when many columns to prevent horizontal overflow.
  // Pages can opt-out by passing compact={false} or override per-column via className.
  const isCompact = compact !== undefined ? compact : columns.length >= 8;
  const compactClass = (() => {
    if (!isCompact) return '';
    return columns.length >= 10 ? 'px-2' : 'px-3';
  })();

  const getCellClass = (column) => {
    return [column.className, compactClass].filter(Boolean).join(' ') || undefined;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (isControlled) {
      onSearch(value);
    } else {
      setInternalSearch(value);
    }
  };

  return (
    <div className="space-y-4 min-w-0" data-testid={testId}>
      {/* Toolbar */}
    {(!hideSearch || customFilter || toolbarExtra || !hideExport) && (
      <div className="flex items-center gap-3 flex-wrap" data-testid="data-table-toolbar">
        {!hideSearch ? (
          <SearchInput
            className="max-w-none flex-1 min-w-[200px]"
            value={searchValue}
            onChange={(next) => handleSearchChange({ target: { value: next } })}
            placeholder={searchPlaceholder}
            testId="data-table-search-input"
          />
        ) : (
          // No in-table search: a flexible spacer keeps the filter/export
          // controls anchored to the right on the same line (Loans-style).
          <div className="flex-1" aria-hidden />
        )}
        {customFilter}
        {toolbarExtra}
        {!hideExport && (
          <Button
            variant="outline"
            data-testid="data-table-export-button"
            disabled={exporting}
          onClick={() => {
              if (exporting) return;
            if (onExport) {
                onExport();
              } else {
                // Default CSV export using visible columns and data
                const exportColumns = columns.filter((c) => c.key !== 'actions');
                const header = exportColumns.map((c) => c.label).join(',');
                const rows = displayData.map((row) =>
                  exportColumns
                    .map((c) => {
                      const val = row[c.key];
                      const str = val == null ? '' : String(val);
                      return `"${str.replace(/"/g, '""')}"`;
                    })
                    .join(',')
                );
                const csv = [header, ...rows].join('\n');
                // UTF-8 BOM so Excel decodes ₹ and other multi-byte chars correctly.
                const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${exportFilename}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        )}
      </div>
    )}

      {/* Table */}
      <div className="rounded-lg border bg-card overflow-x-auto">
        <Table style={{ tableLayout: 'fixed', minWidth: (!fixedLayout && columns.length >= 8) ? `${columns.length * 120}px` : undefined, width: '100%' }}>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={getCellClass(column)}
                >
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              // Loading skeletons
              ['skel-1','skel-2','skel-3','skel-4','skel-5'].map(skelId => (
                <TableRow key={skelId}>
                  {columns.map((column) => (
                    <TableCell key={column.key} className={getCellClass(column)}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {!loading && displayData.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-32 text-center text-muted-foreground">
                  No data found
                </TableCell>
              </TableRow>
            )}
            {!loading && displayData.length > 0 && (
              displayData.map((row) => (
                <TableRow
                  key={row.id || row.loan_number || row.repayment_number || JSON.stringify(row).slice(0, 50)}
                  className={onRowClick ? 'cursor-pointer table-row-interactive' : ''}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column) => (
                    <TableCell key={column.key} className={getCellClass(column)}>
                      {column.render ? column.render(row) : row[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination — spaced below the bordered table so it does not look attached */}
      {pagination && (
        <div className="flex items-center justify-between pt-4 pb-2 px-1 flex-wrap gap-4" data-testid="data-table-pagination">
          <p className="text-sm text-muted-foreground">
            {!isControlled && internalSearch.trim() ? (
              <>Showing {Math.min(displayData.length, pagination.from ? (pagination.to - pagination.from + 1) : displayData.length)} of {displayData.length} results (filtered from {data.length})</>
            ) : (
              <>Showing {pagination.from} to {pagination.to} of {pagination.total} results</>
            )}
          </p>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={pagination.page === 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              data-testid="data-table-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium text-slate-700 px-1">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              data-testid="data-table-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  onSearch: PropTypes.func,
  searchValue: PropTypes.string,
  searchKeys: PropTypes.array,
  onRowClick: PropTypes.func,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    pageSize: PropTypes.number,
    totalPages: PropTypes.number,
    total: PropTypes.number,
    from: PropTypes.number,
    to: PropTypes.number,
    onPageChange: PropTypes.func,
  }),
  hideSearch: PropTypes.bool,
  testId: PropTypes.string,
  onExport: PropTypes.func,
  exporting: PropTypes.bool,
  exportFilename: PropTypes.string,
  hideExport: PropTypes.bool,
  compact: PropTypes.bool,
  fixedLayout: PropTypes.bool,
  toolbarFilter: PropTypes.node,
  filters: PropTypes.node,
  filterContent: PropTypes.node,
  toolbarExtra: PropTypes.node,
};
